// React
import React, { useEffect, useState, useMemo } from 'react'
import { useOutletContext, Outlet, useParams } from 'react-router-dom'
import { LevelContext } from '../../services/LevelContext'

// Antd and MDI
import { Layout, Form, notification } from 'antd'
// Components
import SearchHeader from '../layout/SearchHeader'
import DynamicTable from '../../containers/DynamicTable'
import Pagination from '../utility/Pagination'
import TableHeader from '../utility/TableHeader'

// Utils
import { PagesEnum, SearchSegmentsEnum } from '../../utils/Enums'
import { Loader } from '../../utils/Functions'
import { trackerManagementColumns } from '../../utils/TableColumns'
// Recoil
import * as AllAtoms from '../../recoil/atoms'
import { trackersSelector } from '../../recoil/selectors'
import { useRecoilValue } from 'recoil'

// Hooks
import useFetchQuery from '../../hooks/useFetchQuery'

// ERROR BOUNDARY
import { ErrorBoundary } from 'react-error-boundary'
import FallBackUI from '../errors/FallBackUI'
import { filterDataFunction } from '../../utils/Functions'
import { useChangeTrackerStatus, useDisconnetTracker } from '../../hooks/modalFinishHooks'

const { Content, Footer } = Layout

const TrackerManagementTable = () => {
	const { modalType } = useParams()
	const { collection } = useOutletContext()
	const segment = useMemo(() => Object.values(PagesEnum[collection]?.allowedSegments)[0], [collection])

	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [keyword, setKeyword] = useState('')

	const [disconnectFinishFunction] = useDisconnetTracker()
	const [availabilityFinishFunction] = useChangeTrackerStatus()

	const [promiseLoading, setPromiseLoading] = useState(false)
	const [showActiveTrackers, setShowActiveTrackers] = useState(false)
	const [showConnectedTrackers, setShowConnectedTrackers] = useState(false)

	const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 10 })
	const [searchData, setSearchData] = useState({ data: [], collection: '' })
	const [filterProperty, setFilterProperty] = useState(SearchSegmentsEnum[segment]?.filterProperties?.[segment]?.[0])
	const [filteredOrdersCount, setFilteredOrdersCount] = useState(0)

	const user = useRecoilValue(AllAtoms.userAtom)
	const collectionData = useRecoilValue(trackersSelector({ showActiveTrackers, showConnectedTrackers }))

	useFetchQuery({
		segment: 'Trackers',
		action: 'normal',
	})

	const queryResult = useFetchQuery({ segment: 'Jobs', action: 'normal', variables: { status: 'Job Ongoing' } })

	useEffect(() => {
		setLoading(queryResult?.loading)
	}, [collection, queryResult]) // eslint-disable-line

	useEffect(() => {
		setPageInfo({ pageNo: 1, pageSize: 10 })
	}, [segment]) // eslint-disable-line

	useEffect(() => {
		setPageInfo((prev) => ({ ...prev, pageNo: 1 }))
	}, [keyword]) // eslint-disable-line

	useEffect(() => {
		const FilteredData = filterDataFunction({
			keyword,
			segment,
			filterProperty,
			FilteredOrders: collectionData,
		})
		setFilteredOrdersCount(FilteredData?.length)
		setSearchData({
			data: FilteredData?.slice(pageInfo.pageNo * pageInfo.pageSize - pageInfo.pageSize, pageInfo.pageNo * pageInfo.pageSize),
			collection: collection,
		})
	}, [collectionData, keyword, filterProperty, segment, collection, pageInfo])

	const handleFinish = (record) => {
		setPromiseLoading({ [record?._id]: true })
		disconnectFinishFunction({
			segment,
			props: {
				data: {
					record,
				},
			},
		})
			.then((response) => response)
			.then((queryresult) => {
				if (queryresult?.type) {
					notification[queryresult?.type]({
						message: queryresult.message,
						description: queryresult.description,
						duration: '5',
					})
				}
				setPromiseLoading(false)
			})
			.catch((e) => ({ type: 'error', message: 'Error', description: e.message }))
	}

	const handleChange = (changedValues) => {
		const id = Object.keys(changedValues)?.[0]
		const status = Object.values(changedValues)?.[0]?.status
		form
			.validateFields()
			.then(async () => {
				setPromiseLoading({ [id]: true })
				return await availabilityFinishFunction({
					segment,
					status,
					props: {
						data: {
							record: collectionData?.filter((item) => item?._id === id)?.[0],
						},
					},
				})
					.then((response) => response)
					.catch((e) => ({ type: 'error', message: 'Error', description: e.message }))
			})
			.then((queryresult) => {
				if (queryresult?.type) {
					notification[queryresult?.type]({
						message: queryresult.message,
						description: queryresult.description,
						duration: '5',
					})
				}
				setPromiseLoading(false)
			})
			.catch((e) => {
				console.log(e)
			})
	}

	return (
		<>
			<Layout className='!bg-secondaryBackground'>
				<SearchHeader
					segment={segment}
					keyword={keyword}
					collection={collection}
					setKeyword={setKeyword}
					collectionData={collectionData}
					filterProperty={filterProperty}
					showActiveTrackers={showActiveTrackers}
					setShowActiveTrackers={setShowActiveTrackers}
					showConnectedTrackers={showConnectedTrackers}
					setShowConnectedTrackers={setShowConnectedTrackers}
					setFilterProperty={setFilterProperty}
					setFilteredOrdersCount={setFilteredOrdersCount}
				/>
				{loading ? (
					<Loader />
				) : (
					<>
						<Layout className='!bg-white rounded-lg overflow-y-hidden shadow-[0_1px_8px_rgba(0,0,0,0.16)]'>
							<Content>
								<TableHeader segment={segment} filteredCount={filteredOrdersCount} totalCount={collectionData?.length} collection={collection} />
								<ErrorBoundary
									fallbackRender={({ error, resetErrorBoundary }) => <FallBackUI component={'DynamicTable'} error={error} resetErrorBoundary={resetErrorBoundary} />}
									resetKeys={[searchData]}
								>
									<Form form={form} onValuesChange={handleChange}>
										<DynamicTable
											segment={segment}
											searchData={searchData}
											collection={collection}
											rowClassName='!h-14'
											tableHeight='calc(100vh - 360px)'
											columns={trackerManagementColumns}
											columnProps={{ user, promiseLoading, handleFinish }}
										/>
									</Form>
								</ErrorBoundary>
							</Content>
							<Footer className='flex justify-center items-center !h-16 !bg-white !py-2 !border-t-[0.5px] !border-[rgba(0, 0, 0, 0.12)]'>
								<Pagination setPageInfo={setPageInfo} totalCount={filteredOrdersCount} pageInfo={pageInfo}></Pagination>
							</Footer>
						</Layout>
					</>
				)}
			</Layout>
			<LevelContext.Provider
				value={{
					loading,
					modalType,
					props: {
						segment,
						data: {},
					},
				}}
			>
				<Outlet />
			</LevelContext.Provider>
		</>
	)
}

export default TrackerManagementTable
